import { SVForm, SVPasswordInput, SVButton } from '@/components/core';
import PasswordRules from '@/components/PasswordRules';
import { FORM_FIELDS } from '@/constants/formFields';
import { MESSAGES } from '@/constants/messages';
import { ROUTES } from '@/constants/routes';
import { useResetPassword } from '@/hooks/requests/useAuth';
import { logAnalyticsEvent } from '@/utils/analytics';
import showToast from '@/utils/toast';
import { Card, CardContent, Stack, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

type SetPasswordFormFields = {
  password: string;
  confirmPassword: string;
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const { uuid = '' } = useParams();
  const { resetPassword, isLoadingResetPassword } = useResetPassword();
  const formMethods = useForm<SetPasswordFormFields>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  async function onResetPassword(data: SetPasswordFormFields) {
    try {
      const response = await resetPassword({
        setPasswordCode: uuid,
        password: data.password,
      });

      if (response.status === 'expired') {
        logAnalyticsEvent({
          category: 'error',
          action: 'resetError expired',
        });
        showToast({
          type: 'error',
          data: {
            content:
              'Reset password code has expired. Please request another one',
          },
        });
        return;
      } else if (response.status === 'invalid') {
        logAnalyticsEvent({
          category: 'error',
          action: 'resetError invalid',
        });
        showToast({
          type: 'error',
          data: { content: MESSAGES.genericError },
        });
        return;
      }

      logAnalyticsEvent({
        category: 'success',
        action: 'resetSuccess',
      });
      navigate(ROUTES.LOGIN, {
        replace: true,
      });
      showToast({
        type: 'success',
        data: { content: 'Your password has been successfully reset' },
      });
    } catch {
      logAnalyticsEvent({
        category: 'error',
        action: 'resetError',
      });
      showToast({
        type: 'error',
        data: { content: MESSAGES.genericError },
      });
    }
  }

  return (
    <Card>
      <CardContent>
        <Stack spacing={3}>
          <Typography
            textAlign="left"
            sx={{ fontSize: 36 }}
            color="text.secondary"
            gutterBottom
            mb={2}>
            Set Password
          </Typography>
          <Typography
            textAlign="left"
            sx={{ fontSize: 18 }}
            color="text.secondary"
            gutterBottom
            mb={3}>
            Enter a password. Passwords must meet the following criteria:
          </Typography>
          <PasswordRules />
          <SVForm<SetPasswordFormFields>
            methods={formMethods}
            onSubmit={onResetPassword}>
            <Stack spacing={2}>
              <SVPasswordInput name="password" {...FORM_FIELDS.PASSWORD} />
              <SVPasswordInput
                name="confirmPassword"
                passwordName="password"
                {...FORM_FIELDS.CONFIRM_PASSWORD}
              />
              <SVButton
                fullWidth
                type="submit"
                isLoading={isLoadingResetPassword}>
                Submit
              </SVButton>
            </Stack>
          </SVForm>
        </Stack>
      </CardContent>
    </Card>
  );
}
